const LS_DEVICE_ID = 'accounts.deviceId';
const LS_SESSION_TOKEN = 'accounts.sessionToken';
const LS_DEVICE_TRUST_TOKEN = 'accounts.deviceTrustToken';
const LS_USER_ID = 'accounts.userId';
const LS_EMAIL = 'accounts.email';
const LS_TRUST_WINDOW_DAYS = 'accounts.trustWindowDays';

/** Stable per-browser-profile ID the server keys device_trust rows on. Never sent anywhere but this API. */
export function getDeviceId(): string {
  let id = localStorage.getItem(LS_DEVICE_ID);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(LS_DEVICE_ID, id);
  }
  return id;
}

export interface StoredSession {
  sessionToken: string;
  userId: string;
  email: string;
  trustWindowDays: number;
  deviceTrustToken?: string;
}

export function getStoredSession(): StoredSession | null {
  const sessionToken = localStorage.getItem(LS_SESSION_TOKEN);
  const userId = localStorage.getItem(LS_USER_ID);
  const email = localStorage.getItem(LS_EMAIL);
  if (!sessionToken || !userId || !email) return null;
  const trustWindowDays = Number(localStorage.getItem(LS_TRUST_WINDOW_DAYS) ?? '7');
  const deviceTrustToken = localStorage.getItem(LS_DEVICE_TRUST_TOKEN) ?? undefined;
  return { sessionToken, userId, email, trustWindowDays, deviceTrustToken };
}

export function setStoredSession(session: StoredSession): void {
  localStorage.setItem(LS_SESSION_TOKEN, session.sessionToken);
  localStorage.setItem(LS_USER_ID, session.userId);
  localStorage.setItem(LS_EMAIL, session.email);
  localStorage.setItem(LS_TRUST_WINDOW_DAYS, String(session.trustWindowDays));
  if (session.deviceTrustToken) localStorage.setItem(LS_DEVICE_TRUST_TOKEN, session.deviceTrustToken);
}

export function setTrustWindowDays(days: number): void {
  localStorage.setItem(LS_TRUST_WINDOW_DAYS, String(days));
}

/** Whether this device has previously completed a passkey ceremony (may since be expired server-side). */
export function hasDeviceTrustRecord(): boolean {
  return localStorage.getItem(LS_DEVICE_TRUST_TOKEN) !== null;
}

export function clearStoredSession(): void {
  for (const key of [LS_SESSION_TOKEN, LS_DEVICE_TRUST_TOKEN, LS_USER_ID, LS_EMAIL, LS_TRUST_WINDOW_DAYS]) {
    localStorage.removeItem(key);
  }
}

/**
 * The unwrapped master key (K) lives in memory only, never persisted — a page reload always
 * requires re-deriving it via a fresh passkey PRF ceremony. This is a deliberate trade-off:
 * weaker "stay unlocked" UX in exchange for K never touching disk in plaintext.
 */
let masterKeyMemory: CryptoKey | null = null;

export function getMasterKey(): CryptoKey | null {
  return masterKeyMemory;
}

export function setMasterKey(key: CryptoKey | null): void {
  masterKeyMemory = key;
}
